import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useOutletContext } from 'react-router-dom';
import {
  Plus, Search, Edit, Trash2, MoreHorizontal, Image, DollarSign,
  Tag, Grid, List, RefreshCw, ChevronRight, Eye, EyeOff, Copy,
  ArrowUpDown, Filter, Upload
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import axios from 'axios';

import { API } from '@/config/api';

function MenuItem({ item, onEdit, onDelete, onToggle }) {
  return (
    <Card className={`bg-white border-0 shadow-sm ${!item.is_available ? 'opacity-60' : ''}`}>
      <CardContent className="p-3 md:p-4">
        <div className="flex gap-3 md:gap-4">
          {/* Image */}
          <div className="h-16 w-16 md:h-20 md:w-20 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden">
            {item.image_url ? (
              <img 
                src={item.image_url} 
                alt={item.name} 
                className="h-full w-full object-cover"
                onError={(e) => { e.target.src = ''; e.target.className = 'hidden'; }}
              />
            ) : (
              <div className="h-full w-full flex items-center justify-center text-xl md:text-2xl">🍽️</div>
            )}
          </div>
          
          {/* Details */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <h3 className="font-semibold text-gray-900 truncate text-sm md:text-base">{item.name}</h3>
                <p className="text-xs md:text-sm text-gray-500 line-clamp-2">{item.description}</p>
              </div>
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-7 w-7 md:h-8 md:w-8 flex-shrink-0">
                    <MoreHorizontal className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => onEdit(item)}>
                    <Edit className="h-4 w-4 mr-2" />
                    Edit
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => onToggle(item)}>
                    {item.is_available ? (
                      <><EyeOff className="h-4 w-4 mr-2" />Hide from Menu</>
                    ) : (
                      <><Eye className="h-4 w-4 mr-2" />Show on Menu</>
                    )}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => copyText(item.id, 'ID copied')}>
                    <Copy className="h-4 w-4 mr-2" />
                    Copy ID
                  </DropdownMenuItem>
                  <DropdownMenuItem className="text-red-600" onClick={() => onDelete(item)}>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
            
            <div className="flex items-center gap-2 md:gap-3 mt-2 flex-wrap">
              <span className="text-base md:text-lg font-bold text-blue-600">${item.price?.toFixed(2)}</span>
              <Badge variant="outline" className="text-xs">{item.category_name || 'Uncategorized'}</Badge>
              {!item.is_available && <Badge variant="secondary" className="text-xs">Hidden</Badge>}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

import ImageUpload from '@/components/shared/ImageUpload';
import { copyText } from '@/lib/copyText';

export default function MenuPage() {
  const context = useOutletContext() || {};
  const { token = localStorage.getItem('backoffice_token'), manager = null } = context;
  
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  
  // Dialog states
  const [showItemDialog, setShowItemDialog] = useState(false);
  const [showCategoryDialog, setShowCategoryDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);
  
  // Form states
  const [itemForm, setItemForm] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    image_url: '',
    is_available: true
  });
  
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    description: ''
  });

  const getHeaders = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  const fetchMenuData = useLatestCallback(async () => {
    setLoadError(false);
    try {
      const [itemsRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/menu-mgmt/items`, getHeaders()),
        axios.get(`${API}/menu-mgmt/categories`, getHeaders())
      ]);
      
      setItems(itemsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      console.error('Failed to fetch menu data:', error);
      toast.error('Could not load the menu');
      setCategories([]);
      setItems([]);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    if (token) {
      fetchMenuData();
    }
  }, [token, fetchMenuData]);

  const handleSaveItem = async () => {
    try {
      if (editingItem) {
        await axios.put(
          `${API}/menu-mgmt/items/${editingItem.id}`,
          { ...itemForm, price: parseFloat(itemForm.price) },
          getHeaders()
        );
        toast.success('Item updated successfully');
      } else {
        await axios.post(
          `${API}/menu-mgmt/items`,
          { ...itemForm, price: parseFloat(itemForm.price), restaurant_id: manager?.restaurant_id },
          getHeaders()
        );
        toast.success('Item added successfully');
      }
      setShowItemDialog(false);
      resetItemForm();
      fetchMenuData();
    } catch (error) {
      toast.error('Failed to save item');
    }
  };

  const handleDeleteItem = async (item) => {
    if (!confirm(`Delete "${item.name}"?`)) return;
    
    try {
      await axios.delete(`${API}/menu-mgmt/items/${item.id}`, getHeaders());
      toast.success('Item deleted');
      fetchMenuData();
    } catch (error) {
      toast.error('Failed to delete item');
    }
  };

  const handleToggleAvailability = async (item) => {
    try {
      await axios.put(
        `${API}/menu-mgmt/items/${item.id}`,
        { ...item, is_available: !item.is_available },
        getHeaders()
      );
      toast.success(item.is_available ? 'Item hidden from menu' : 'Item shown on menu');
      fetchMenuData();
    } catch (error) {
      toast.error('Failed to update item');
    }
  };

  const handleSaveCategory = async () => {
    try {
      if (editingCategory) {
        await axios.put(
          `${API}/menu-mgmt/categories/${editingCategory.id}`,
          categoryForm,
          getHeaders()
        );
        toast.success('Category updated');
      } else {
        await axios.post(
          `${API}/menu-mgmt/categories`,
          { ...categoryForm, restaurant_id: manager?.restaurant_id },
          getHeaders()
        );
        toast.success('Category added');
      }
      setShowCategoryDialog(false);
      resetCategoryForm();
      fetchMenuData();
    } catch (error) {
      toast.error('Failed to save category');
    }
  };

  const resetItemForm = () => {
    setItemForm({
      name: '',
      description: '',
      price: '',
      category_id: '',
      image_url: '',
      is_available: true
    });
    setEditingItem(null);
  };

  const resetCategoryForm = () => {
    setCategoryForm({ name: '', description: '' });
    setEditingCategory(null);
  };

  const openEditItem = (item) => {
    setItemForm({
      name: item.name,
      description: item.description || '',
      price: item.price?.toString() || '',
      category_id: item.category_id?.toString() || '',
      image_url: item.image_url || '',
      is_available: item.is_available
    });
    setEditingItem(item);
    setShowItemDialog(true);
  };

  // Filter items
  const filteredItems = items.filter(item => {
    if (selectedCategory !== 'all' && item.category_id?.toString() !== selectedCategory) return false;
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return item.name?.toLowerCase().includes(query) || item.description?.toLowerCase().includes(query);
    }
    return true;
  });

  return (
    <div className="space-y-4 md:space-y-6" data-testid="menu-page">
      {loadError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 flex items-center justify-between gap-3" data-testid="menu-load-error">
          <span>Could not load the menu.</span>
          <Button variant="outline" size="sm" onClick={fetchMenuData}>Try again</Button>
        </div>
      )}
      {/* Header - Stack on mobile */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-gray-900">Menu Management</h1>
          <p className="text-gray-500 text-sm md:text-base">Manage your menu items and categories</p>
        </div>
        <div className="flex items-center gap-2 sm:gap-3">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => { resetCategoryForm(); setShowCategoryDialog(true); }}
            className="flex-1 sm:flex-none"
          >
            <Tag className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add </span>Category
          </Button>
          <Button 
            size="sm"
            className="bg-blue-600 hover:bg-blue-700 flex-1 sm:flex-none"
            onClick={() => { resetItemForm(); setShowItemDialog(true); }}
          >
            <Plus className="h-4 w-4 mr-1 sm:mr-2" />
            <span className="hidden sm:inline">Add </span>Item
          </Button>
        </div>
      </div>

      {/* Filters & Search - Responsive */}
      <Card className="bg-white border-0 shadow-sm">
        <CardContent className="p-3 md:p-4">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Search menu items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            
            <div className="flex gap-2">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-full sm:w-40">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <div className="flex border rounded-lg flex-shrink-0">
                <Button 
                  variant={viewMode === 'grid' ? 'secondary' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('grid')}
                >
                  <Grid className="h-4 w-4" />
                </Button>
                <Button 
                  variant={viewMode === 'list' ? 'secondary' : 'ghost'} 
                  size="sm"
                  onClick={() => setViewMode('list')}
                >
                  <List className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Categories Quick Access - Horizontal scroll on mobile */}
      <div className="flex gap-2 overflow-x-auto pb-2 -mx-4 px-4 md:mx-0 md:px-0">
        <Button
          variant={selectedCategory === 'all' ? 'default' : 'outline'}
          size="sm"
          onClick={() => setSelectedCategory('all')}
          className="flex-shrink-0"
        >
          All ({items.length})
        </Button>
        {categories.map(cat => {
          const count = items.filter(i => i.category_id === cat.id).length;
          return (
            <Button
              key={cat.id}
              variant={selectedCategory === cat.id.toString() ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSelectedCategory(cat.id.toString())}
              className="flex-shrink-0"
            >
              {cat.name} ({count})
            </Button>
          );
        })}
      </div>

      {/* Items Grid/List - Responsive grid */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
        </div>
      ) : filteredItems.length > 0 ? (
        <div className={viewMode === 'grid' ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4' : 'space-y-3'}>
          {filteredItems.map(item => (
            <MenuItem
              key={item.id}
              item={item}
              onEdit={openEditItem}
              onDelete={handleDeleteItem}
              onToggle={handleToggleAvailability}
            />
          ))}
        </div>
      ) : (
        <Card className="bg-white border-0 shadow-sm">
          <CardContent className="py-12 md:py-20 text-center">
            <Image className="h-12 w-12 md:h-16 md:w-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-base md:text-lg font-semibold text-gray-700">No menu items found</h3>
            <p className="text-gray-500 mb-4 text-sm md:text-base">Add items to start building your menu</p>
            <Button 
              onClick={() => { resetItemForm(); setShowItemDialog(true); }}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add First Item
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Add/Edit Item Dialog */}
      <Dialog open={showItemDialog} onOpenChange={setShowItemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Item' : 'Add New Item'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Item Name *</Label>
              <Input
                value={itemForm.name}
                onChange={(e) => setItemForm({ ...itemForm, name: e.target.value })}
                placeholder="e.g., Classic Burger"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={itemForm.description}
                onChange={(e) => setItemForm({ ...itemForm, description: e.target.value })}
                placeholder="Describe this item..."
                rows={2}
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Price *</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    type="number"
                    step="0.01"
                    value={itemForm.price}
                    onChange={(e) => setItemForm({ ...itemForm, price: e.target.value })}
                    placeholder="0.00"
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label>Category</Label>
                <Select
                  value={itemForm.category_id}
                  onValueChange={(val) => setItemForm({ ...itemForm, category_id: val })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id.toString()}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div>
              <Label>Image</Label>
              <ImageUpload
                value={itemForm.image_url}
                onChange={(url) => setItemForm({ ...itemForm, image_url: url })}
                folder="menu"
                className="mt-1"
              />
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Available on Menu</Label>
              <Switch
                checked={itemForm.is_available}
                onCheckedChange={(checked) => setItemForm({ ...itemForm, is_available: checked })}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowItemDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveItem} disabled={!itemForm.name || !itemForm.price}>
              {editingItem ? 'Save Changes' : 'Add Item'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add/Edit Category Dialog */}
      <Dialog open={showCategoryDialog} onOpenChange={setShowCategoryDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>{editingCategory ? 'Edit Category' : 'Add Category'}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Category Name *</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm({ ...categoryForm, name: e.target.value })}
                placeholder="e.g., Appetizers"
              />
            </div>
            
            <div>
              <Label>Description</Label>
              <Textarea
                value={categoryForm.description}
                onChange={(e) => setCategoryForm({ ...categoryForm, description: e.target.value })}
                placeholder="Optional description..."
                rows={2}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryDialog(false)}>Cancel</Button>
            <Button onClick={handleSaveCategory} disabled={!categoryForm.name}>
              {editingCategory ? 'Save' : 'Add Category'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
